/**
 * features/common/SendDocumentModal.tsx
 * Generic "email a PDF" modal. The parent precomputes the PDF base64
 * and passes recipient/subject/message; this calls sendAcepsleEmail.
 */
import { useState } from 'react';
import { X, Send } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';
import { glassSurface } from '@/components/ui/glass';
import { Button, Field, Input, Textarea } from '@/components/ui/primitives';

interface Attachment {
  filename: string;
  contentBase64: string;
  contentType: string;
}
interface SendPayload {
  to: string;
  cc?: string;
  subject: string;
  body: string;
  attachments?: Attachment[];
  relatedEntityType?: string;
  relatedEntityId?: string;
}
const sendEmail = httpsCallable<SendPayload, { ok: boolean }>(functions, 'sendAcepsleEmail');

export function SendDocumentModal({
  heading,
  to: toInit,
  subject: subjectInit,
  message: messageInit,
  filename,
  contentBase64,
  relatedEntityType,
  relatedEntityId,
  onClose,
}: {
  heading: string;
  to: string;
  subject: string;
  message: string;
  filename: string;
  contentBase64: string;
  relatedEntityType: string;
  relatedEntityId: string;
  onClose: () => void;
}) {
  const [to, setTo] = useState(toInit);
  const [subject, setSubject] = useState(subjectInit);
  const [message, setMessage] = useState(messageInit);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  async function send() {
    if (!to.trim()) {
      setError('Enter a recipient email.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await sendEmail({
        to: to.trim(),
        subject: subject.trim(),
        body: message,
        attachments: [{ filename, contentBase64, contentType: 'application/pdf' }],
        relatedEntityType,
        relatedEntityId,
      });
      setSent(true);
      setTimeout(onClose, 900);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not send.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/40 p-4 backdrop-blur-md">
      <div className="my-12 w-full max-w-lg">
        <div className={glassSurface('space-y-4 p-5')}>
          <div className="-mx-5 -mt-5 mb-1 flex items-center justify-between rounded-t-card bg-brand px-5 py-3.5">
            <div className="flex items-center gap-2 text-white">
              <Send size={18} />
              <h2 className="text-base font-semibold text-white">{heading}</h2>
            </div>
            <button
              onClick={onClose}
              className="rounded-lg p-1 text-white/80 transition hover:bg-white/15 hover:text-white"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>

          <Field label="To">
            <Input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="client@email.com" />
          </Field>
          <Field label="Subject">
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
          </Field>
          <Field label="Message">
            <Textarea rows={6} value={message} onChange={(e) => setMessage(e.target.value)} />
          </Field>

          <p className="text-xs text-primaryLight dark:text-gray-400">
            Attaches <span className="font-semibold">{filename}</span> automatically.
          </p>

          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
          {sent && <p className="text-sm font-semibold text-green-600 dark:text-green-400">Sent ✓</p>}

          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button icon={<Send size={16} />} onClick={send} loading={busy} disabled={sent}>
              Send
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
